/**
 * 📖 Message Processor - Procesamiento de mensajes y markdown
 * @version 1.0.0
 * @description Clase para manejar el procesamiento de mensajes y markdown
 */

export class MessageProcessor {
  constructor(config = {}) {
    this.config = {
      debug: false,
      ...config,
    };
  }

  /**
   * 🔍 Detecta si un texto contiene markdown
   * @param {string} text - Texto a analizar
   * @returns {boolean} True si contiene markdown
   */
  isMarkdown(text) {
    if (!text || typeof text !== "string") return false;

    // Patrones comunes de markdown
    const markdownPatterns = [
      /\*\*.*?\*\*/, // **bold**
      /\*.*?\*/, // *italic*
      /_.*?_/, // _italic_
      /`.*?`/, // `code`
      /```[\s\S]*?```/, // ```code block```
      /#{1,6}\s/, // # headers
      /^\s*[-*+]\s/m, // - lista
      /^\s*\d+\.\s/m, // 1. lista numerada
      /\[.*?\]\(.*?\)/, // [link](url)
      /!\[.*?\]\(.*?\)/, // ![image](url)
      /^\s*>\s/m, // > quote
      /^\s*---\s*$/m, // --- horizontal rule
      /\n\n/, // párrafos separados
    ];

    return markdownPatterns.some((pattern) => pattern.test(text));
  }

  /**
   * 📖 Parsea markdown básico a HTML
   * @param {string} text - Texto markdown a convertir
   * @returns {string} HTML resultante
   */
  parseMarkdown(text) {
    if (!text) return "";

    let html = text;

    // Escapar caracteres HTML primero (excepto los que vamos a convertir)
    html = html
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    // Headers (# ## ### etc)
    html = html.replace(/^### (.*$)/gm, "<h3>$1</h3>");
    html = html.replace(/^## (.*$)/gm, "<h2>$1</h2>");
    html = html.replace(/^# (.*$)/gm, "<h1>$1</h1>");

    // Bold y italic
    html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
    html = html.replace(/_(.*?)_/g, "<em>$1</em>");

    // Code blocks
    html = html.replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>");
    html = html.replace(/`(.*?)`/g, "<code>$1</code>");

    // Links
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener">$1</a>'
    );

    // Images
    html = html.replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" style="max-width: 100%; height: auto;">'
    );

    // Listas
    html = html.replace(/^\s*[-*+]\s(.+)$/gm, "<li>$1</li>");
    html = html.replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>");

    // Listas numeradas
    html = html.replace(/^\s*(\d+)\.\s(.+)$/gm, "<li>$2</li>");
    html = html.replace(/(<li>.*<\/li>)/s, function (match) {
      if (match.includes("ul>")) return match;
      return "<ol>" + match + "</ol>";
    });

    // Quotes
    html = html.replace(/^\s*&gt;\s(.+)$/gm, "<blockquote>$1</blockquote>");

    // Horizontal rules
    html = html.replace(/^\s*---\s*$/gm, "<hr>");

    // Saltos de línea y párrafos
    html = html.replace(/\n\n/g, "</p><p>");
    html = "<p>" + html + "</p>";

    // Limpiar párrafos vacíos y etiquetas mal formadas
    html = html.replace(/<p><\/p>/g, "");
    html = html.replace(/<p>(<h[1-6]>.*?<\/h[1-6]>)<\/p>/g, "$1");
    html = html.replace(/<p>(<ul>.*?<\/ul>)<\/p>/gs, "$1");
    html = html.replace(/<p>(<ol>.*?<\/ol>)<\/p>/gs, "$1");
    html = html.replace(/<p>(<blockquote>.*?<\/blockquote>)<\/p>/g, "$1");
    html = html.replace(/<p>(<hr>)<\/p>/g, "$1");
    html = html.replace(/<p>(<pre>.*?<\/pre>)<\/p>/gs, "$1");

    // Saltos de línea simples
    html = html.replace(/\n/g, "<br>");

    return html;
  }

  /**
   * 💬 Crea un mensaje de error amigable para el usuario
   * @param {string} errorMessage - Mensaje de error técnico
   * @param {boolean} showTechnicalDetails - Mostrar detalles técnicos
   * @returns {string} Mensaje de error amigable en HTML
   */
  createUserFriendlyErrorMessage(errorMessage, showTechnicalDetails = false) {
    // Determinar el tipo de error y crear un mensaje apropiado
    let userMessage = "";
    let technicalDetails = "";

    if (
      errorMessage.includes("fetch") ||
      errorMessage.includes("conectividad") ||
      errorMessage.includes("network")
    ) {
      userMessage =
        "🌐 <strong>Error de conexión</strong><br>No se pudo conectar con el servicio. Verifica tu conexión a internet e intenta nuevamente.";
    } else if (
      errorMessage.includes("API key") ||
      errorMessage.includes("401") ||
      errorMessage.includes("Unauthorized")
    ) {
      userMessage =
        "🔑 <strong>Error de autenticación</strong><br>Las credenciales de acceso no son válidas. Contacta al administrador del sistema.";
    } else if (
      errorMessage.includes("404") ||
      errorMessage.includes("Not Found")
    ) {
      userMessage =
        "🔍 <strong>Servicio no disponible</strong><br>El servicio de chat no está disponible en este momento. Revisa el API KEY";
    } else if (
      errorMessage.includes("500") ||
      errorMessage.includes("Internal Server Error")
    ) {
      userMessage =
        "⚠️ <strong>Error del servidor</strong><br>Hay un problema temporal con el servidor. Intenta nuevamente en unos minutos.";
    } else {
      userMessage =
        "❌ <strong>Error de inicialización</strong><br>No se pudo inicializar el chat. Por favor intenta recargar la página.";
    }

    // Agregar detalles técnicos si se requiere
    if (showTechnicalDetails) {
      technicalDetails = `<br><br><small style="color: #64748b; font-style: italic;">Detalles técnicos: ${errorMessage}</small>`;
    }

    return `${userMessage}${technicalDetails}`;
  }

  /**
   * ⚙️ Actualiza la configuración del procesador
   * @param {Object} newConfig - Nueva configuración
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }
}
